//  Copyright (c) 2019-2023 Cigent Technology Inc. All Rights Reserved.
import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {UserService} from '../../../services/user-service';
import {NotificationService} from '../../../services/notification-service';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {Router} from '@angular/router';
import {AlertDialogComponent} from '../../dialogs/alert/alert.component';
import {MatDialog} from '@angular/material/dialog';
import {TranslateService} from '@ngx-translate/core';

@Component({
    selector: 'app-email-verify',
    templateUrl: './email-verify.component.html',
    styleUrls: ['./email-verify.component.scss'],
    standalone: false
})
export class EmailVerifyComponent implements OnInit {

  @Input() address;
  @Input() stripePlanID;
  @Output() onSuccess = new EventEmitter<void>();

  submitting = false;
  verifyCodeFormGroup: UntypedFormGroup;

  fEmailSent: string;
  fAccountExists: string;
  fAccountExistsContentA: string;
  fAccountExistsContentB: string;
  fOK: string;
  fErrorEmailReset: string;
  fTooManyRequests: string;
  fPleaseNote: string;
  fAlreadyVerifiedTitle: string;
  fAlreadyVerifiedMessage: string;
  fWait: string;

  constructor(
      private userService: UserService,
      private router: Router,
      private formBuilder: UntypedFormBuilder,
      private notification: NotificationService,
      private dialog: MatDialog,
      private translate: TranslateService,
  ) {
    const keys = ['EMAIL_SENT', 'SUB_ACC_EXISTS', 'SUB_ACC_EXISTS_A', 'SUB_ACC_EXISTS_B', 'ALERT_OK', 'SUB_WAIT',
      'ERROR_EMAIL_RESET', 'PLEASE_NOTE', 'EMV_ALREADY_VERIFIED_TITLE', 'EMV_ALREADY_VERIFIED_MESSAGE'];
    this.translate.get(keys).subscribe(vals => {
      this.fEmailSent = vals['EMAIL_SENT'];
      this.fAccountExists = vals['SUB_ACC_EXISTS'];
      this.fAccountExistsContentA = vals['SUB_ACC_EXISTS_A'];
      this.fAccountExistsContentB = vals['SUB_ACC_EXISTS_B'];
      this.fOK = vals['ALERT_OK'];
      this.fErrorEmailReset = vals['ERROR_EMAIL_RESET'];
      this.fPleaseNote = vals['PLEASE_NOTE'];
      this.fAlreadyVerifiedTitle = vals['EMV_ALREADY_VERIFIED_TITLE'];
      this.fAlreadyVerifiedMessage = vals['EMV_ALREADY_VERIFIED_MESSAGE'];
      this.fWait = vals['SUB_WAIT'];
    });
  }

  ngOnInit(): void {
    this.verifyCodeFormGroup = this.formBuilder.group({
      verifyCode: ['', Validators.compose([
              Validators.required,
              Validators.minLength(6),
              Validators.maxLength(6)
          ])],
    });
    this.resendVerification(true);
  }

  onResendClick() {
    this.resendVerification();
  }

  resendVerification(suppressMessages=false) {
    if (this.address) {
      this.userService.resendVerificationEmail(this.address).subscribe(
          () => {
            if (!suppressMessages) {
              this.notification.showSuccess(this.fEmailSent);
            }
          },
          errorResponse => {
            if (!suppressMessages) {
              if (errorResponse?.status == 429) {
                const seconds = errorResponse.headers.get('retry-after') || '300';
                const content = this.translate.instant('EMV_TOO_MANY_REQUESTS', {seconds: seconds});
                this.notification.showError(content, this.fPleaseNote);
              } else if (errorResponse?.status == 409) {
                this.alreadyVerified();
              } else {
                this.notification.showError(this.fErrorEmailReset);
              }
            }
          });
    }
  }

  get e() { return this.verifyCodeFormGroup.controls; }

  onSubmitClick() {
    this.submitting = true;
    const email = this.address;
    const code = this.verifyCodeFormGroup.value.verifyCode;
    const stripePlanID = this.stripePlanID;
    this.userService.checkVerificationCode(email, code, stripePlanID).subscribe(
        () => {
          this.onSuccess.emit();
          this.submitting = false;
        },
        errorResponse => {
          if (errorResponse.status == 409) {
            this.alreadyVerified();
          } else {
            this.verifyCodeFormGroup.controls['verifyCode'].setErrors({invalidCode: true});
          }
          this.submitting = false;
        }
    );
  }

  alreadyVerified() {
    const dialogRef = this.dialog.open(AlertDialogComponent, {
      width: '450px',
      data: {
        title: this.fAlreadyVerifiedTitle,
        content: this.fAlreadyVerifiedMessage,
        acceptBtnName: this.fOK,
        showRejectBtn: false,
      },
    });
    dialogRef.afterClosed().subscribe(
        () => {
          return this.router.navigate(['login']);
        }
    );
  }
}
